import { format, formatDistanceToNowStrict, isValid, parse, parseISO } from 'date-fns'
import { tr } from 'date-fns/locale'

/** API stamp: `DD.MM.YYYY HH:mm:ss` (Europe/Istanbul). */
const TR_DATETIME = /^(\d{2})\.(\d{2})\.(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const SENTINEL = /^0001-01-01/

function blank(value: string | null | undefined) {
  const v = value?.trim()
  if (!v || SENTINEL.test(v)) return null
  return v
}

/** Parse API date strings: TR datetime, YYYY-MM-DD, or ISO/RFC3339. */
export function toDate(value: string | null | undefined): Date | null {
  const v = blank(value)
  if (!v) return null

  const trMatch = TR_DATETIME.exec(v)
  if (trMatch) {
    const [, dd, mm, yyyy, hh = '00', min = '00', ss = '00'] = trMatch
    const d = parse(`${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`, 'yyyy-MM-dd HH:mm:ss', new Date())
    return isValid(d) ? d : null
  }

  if (ISO_DATE.test(v)) {
    const d = parseISO(v)
    return isValid(d) ? d : null
  }

  const iso = parseISO(v)
  if (isValid(iso)) return iso

  const fallback = new Date(v)
  return isValid(fallback) ? fallback : null
}

/** API tarih/zaman damgasını yerel `YYYY-MM-DD` gününe çevirir (sıralama, yıl/ay filtreleri için). */
export function isoDay(value: string | null | undefined): string {
  const d = toDate(value)
  if (!d) return ''
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** İş tarihi / kısa tarih. API `YYYY-MM-DD` veya TR datetime. */
export const fmtDate = (value: string | null | undefined) => {
  const v = blank(value)
  if (!v) return '—'
  if (ISO_DATE.test(v)) return v.split('-').reverse().join('.')
  const trMatch = TR_DATETIME.exec(v)
  if (trMatch) return `${trMatch[1]}.${trMatch[2]}.${trMatch[3]}`
  const d = toDate(v)
  return d ? format(d, 'dd.MM.yyyy') : '—'
}

/**
 * Stamp alanları (`created_at` vb.).
 * API zaten `DD.MM.YYYY HH:mm:ss` gönderiyorsa doğrudan gösterilir.
 */
export const fmtDateTime = (value: string | null | undefined) => {
  const v = blank(value)
  if (!v) return '—'
  if (TR_DATETIME.test(v) && v.includes(':')) {
    // Ensure seconds present for stable display
    const m = TR_DATETIME.exec(v)!
    const [, dd, mm, yyyy, hh = '00', min = '00', ss = '00'] = m
    return `${dd}.${mm}.${yyyy} ${hh}:${min}:${ss}`
  }
  const d = toDate(v)
  return d ? format(d, 'dd.MM.yyyy HH:mm:ss') : '—'
}

export const fmtRelative = (value: string | null | undefined) => {
  const d = toDate(value)
  return d ? formatDistanceToNowStrict(d, { locale: tr, addSuffix: true }) : '—'
}

/** Para tutarı API'den string (decimal) gelir; istemci hesap yapmaz, yalnız gösterir. */
export function fmtMoney(amount: string, currency = 'TRY') {
  const n = Number(amount)
  if (!Number.isFinite(n)) return amount
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(n)
}
